import DropdownBox from "src/components/atoms/dropdown/dropdown-box";
import ActionPanel from "../action-panel/action-panel";
import { BusinessViewModel } from "../business-list/business-list.view-model";
import { BusinessNumberViewModel } from "../numbers/number-viewmodel";
import { useAuth } from "../auth/auth-context";
import { useEffect, useRef, useState } from "react";
import {
	IBusinessListModel,
	IFileInfo,
	INewNumberModel,
} from "src/types/models/apiModels";
import ReactDatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import InputBox from "src/components/atoms/v2/inputBox/inputBox";
import UploadButton from "src/components/molecules/upload-button/upload-button";
import BoxWithIcon from "src/components/molecules/box-with-icon/box-with-icon";
import { ReactComponent as CalendarIcon } from "../../../assets/svg/calendarIcon.svg";
import { ReactComponent as DataIcon } from "../../../assets/svg/dataIcon.svg";
import DropdownDefaultBox from "src/components/atoms/dropdown/dropdown-default";

interface ICreateCampaignProps {
	onClose: () => void;
}

const CreateCampaign: React.FC<ICreateCampaignProps> = ({ onClose }) => {
	const { user } = useAuth();
	const viewModel = useRef<BusinessNumberViewModel>();
	const businessListViewModel = useRef<BusinessViewModel | null>(null);
	const [startDate, setStartDate] = useState<Date | null>(new Date());
	const [loading, setLoading] = useState(true);
	const [hasMoreThanOneBusiness, setHasMoreThanOneBusiness] = useState(false);
	const [business, setBusiness] = useState<IBusinessListModel | undefined>(
		undefined
	);
	const [campaignName, setCampaignName] = useState("");
	const [businesses, setBusinesses] = useState<IBusinessListModel[]>([]);
	const [businessNumbers, setBusinessNumbers] = useState<INewNumberModel[]>(
		[]
	);
	const [campaignIntent, setCampaignIntent] = useState("");
	const [disableNumbersDropdown, setDisableNumbersDropdown] = useState(true);
	const [fileSrc, setFileSrc] = useState<IFileInfo | undefined>();

	useEffect(() => {
		if (user && user.accessToken) {
			viewModel.current = new BusinessNumberViewModel(user?.accessToken);
			businessListViewModel.current = new BusinessViewModel(
				user.accessToken
			);
			fetchBusinesses();
		}
	}, []);

	useEffect(() => {
		if (business) {
			fetchBusinessNumbers(business.businessId); // Call the async function inside useEffect
		}
	}, [business]); // Dependency array ensures this runs when businessId changes

	const fetchBusinessNumbers = async (businessId: string) => {
		if (business && viewModel.current) {
			setLoading(true);
			setDisableNumbersDropdown(true);
			try {
				const data = await viewModel.current.getNumbers(businessId);
				const usableNumbers = data.filter((p) => p.status === "Active");

				if (usableNumbers) {
					setBusinessNumbers(usableNumbers);
					setLoading(false);
					setDisableNumbersDropdown(false);
				} else {
					setBusinessNumbers([]);
					setLoading(false);
					setDisableNumbersDropdown(false);
				}
			} catch (error) {
				setBusinessNumbers([]);
				setLoading(false);
				setDisableNumbersDropdown(false);
			}
		}
	};

	const fetchBusinesses = async () => {
		if (user && user.accessToken) {
			viewModel.current = new BusinessNumberViewModel(user?.accessToken);

			try {
				if (user && user.accessToken) {
					setLoading(true);
					businessListViewModel.current = new BusinessViewModel(
						user.accessToken
					);
					const response =
						await businessListViewModel.current?.fetchListAsync();
					setHasMoreThanOneBusiness(response.length > 1);
					setBusinesses(() => response);
					setBusiness(() => response[0]);
					setLoading(false);
				}
			} catch (error) {
				console.error("Error fetching data:", error);
			} finally {
			}
		}
	};

	const getTodayTimeLimit = () => {
		const now = new Date();
		if (startDate?.toDateString() === now.toDateString()) {
			// If the selected date is today, restrict the minimum time to the current time
			return new Date(
				now.getFullYear(),
				now.getMonth(),
				now.getDate(),
				now.getHours(),
				now.getMinutes()
			);
		} else {
			// Allow all times for future dates
			return new Date(0, 0, 0, 0, 0); // Midnight
		}
	};

	const handleFileChange = async (uploadedImage: IFileInfo) => {
		if (uploadedImage) {
			setFileSrc(() => uploadedImage);
		} else {
			setFileSrc(undefined);
		}
	};

	return (
		<div className="flex flex-col items-start justify-center gap-6 w-full">
			<span className="font-Poppins font-semibold text-xl">
				Create branding campaign
			</span>
			<div className="flex flex-col items-center justify-center gap-6 w-full">
				<InputBox
					label={"Campaign name"}
					type={"text"}
					maxLength={100}
					value={campaignName}
					onChange={function (value: string): void {
						setCampaignName(value);
					}}
				></InputBox>
				<div className="flex items-center justify-center gap-4 w-full">
					<div className="flex flex-col items-start justify-center gap-1 w-full">
						<span className="font-Poppins font-semibold text-md">
							Business
						</span>
						<DropdownDefaultBox
							defaultText={
								businesses.length > 0
									? businesses[0].name
									: "Please select a business"
							}
							options={businesses.map((business) => ({
								label: business.name,
								value: business.businessId,
							}))}
							onSelected={async (selectedOption) => {
								const selectedBusiness = businesses.filter(
									(p) => p.businessId === selectedOption.value
								)[0];
								setBusiness(selectedBusiness);
							}}
						></DropdownDefaultBox>
					</div>
					<div className="flex flex-col items-start justify-center gap-1 w-full">
						<span className="font-Poppins font-semibold text-md">
							Branded numbers
						</span>
						<DropdownDefaultBox
							disabled={disableNumbersDropdown}
							defaultText={
								businessNumbers.length === 1
									? businessNumbers[0].number
									: "Please select branded number"
							}
							options={businessNumbers.map((number) => ({
								label: number.number,
								value: number.number,
							}))}
							onSelected={(selectedOption): void => {
								console.log(selectedOption.label);
							}}
							font={"font-code"}
						></DropdownDefaultBox>
					</div>
				</div>
			</div>
			<div className="flex flex-col items-center justify-center w-full gap-8">
				<InputBox
					label={"Campaign intent"}
					type={"text"}
					maxLength={32}
					value={campaignIntent}
					onChange={function (value: string): void {
						setCampaignIntent(value);
					}}
					helpText="If a campaign intent is not provided, default intent on the branded number will be used."
				></InputBox>
				<BoxWithIcon
					icon={<CalendarIcon className="h-5 w-5"></CalendarIcon>}
					title={"Schedule"}
				>
					<div className="flex items-start justify-center gap-4 w-full">
						<div className="bg-gray-50 flex flex-col items-start justify-center gap-4 p-4 border border-dashed w-full">
							<span className="font-Poppins font-semibold text-md text-nowrap">
								Start date time
							</span>
							<ReactDatePicker
								selected={startDate}
								onChange={(date) => setStartDate(date)}
								showTimeSelect
								dateFormat="MMMM d, yyyy h:mm aa"
								minDate={new Date()} // Disable all past dates
								minTime={getTodayTimeLimit()} // Dynamically disable past times for today's date
								maxTime={new Date(0, 0, 0, 23, 59)} // Set the maximum time for any day (23:59)
								className="cursor-pointer w-full font-Poppins text-md border-gray-300 shadow-sm focus:ring focus:ring-blue-200 focus:ring-opacity-50 border-b-2 text-center"
								wrapperClassName="w-[270px]"
								popperClassName="z-[9999]" // Ensure it appears on top
								portalId="root" // Render at the top of the DOM to avoid being hidden
							/>

							<span className="font-Poppins font-semibold text-md">
								Repeats
							</span>
							<div>
								<DropdownDefaultBox
									disabled={disableNumbersDropdown}
									defaultText={"Does not repeat"}
									options={[
										{
											label: "Does not repeat",
											value: "Does not repeat",
										},
										{
											label: "Every weekday (Mon-Fri)",
											value: "Every weekday (Mon-Fri)",
										},
										{ label: "Daily", value: "Daily" },
										{ label: "Week", value: "Week" },
										{ label: "Monthly", value: "Monthly" },
										{ label: "Yearly", value: "Yearly" },
									]}
									onSelected={(selectedOption): void => {
										console.log(selectedOption.label);
									}}
								></DropdownDefaultBox>
							</div>

							<span className="font-Poppins font-semibold text-md text-nowrap">
								Repeat every
							</span>
							<div className="w-fit">
								<InputBox
									label={""}
									type={"number"}
									value="1"
									onChange={function (value: string): void {
										console.log(`value`, value);
									}}
								></InputBox>
							</div>
							<div className="w-fit">
								<DropdownBox
									disabled={disableNumbersDropdown}
									defaultText={"Day"}
									options={[
										{ label: "Day", value: "Day" },
										{ label: "Week", value: "Week" },
										{ label: "Month", value: "Month" },
										{ label: "Year", value: "Year" },
									]}
									onSelected={(selectedOption): void => {
										console.log(selectedOption.label);
									}}
								></DropdownBox>
							</div>
							<span className="font-Poppins font-semibold text-md text-nowrap">
								End date time
							</span>
							<ReactDatePicker
								selected={startDate}
								onChange={(date) => setStartDate(date)}
								dateFormat="MMMM d, yyyy"
								minDate={new Date()} // Disable all past dates
								minTime={getTodayTimeLimit()} // Dynamically disable past times for today's date
								maxTime={new Date(0, 0, 0, 23, 59)} // Set the maximum time for any day (23:59)
								className="cursor-pointer w-full font-Poppins text-md border-gray-300 shadow-sm focus:ring focus:ring-blue-200 focus:ring-opacity-50 border-b-2 text-center"
								wrapperClassName="w-[250px]"
								popperClassName="z-[9999]" // Ensure it appears on top
								portalId="root" // Render at the top of the DOM to avoid being hidden
							/>
						</div>
						<div className="flex items-end justify-center gap-2"></div>
					</div>
				</BoxWithIcon>
				<BoxWithIcon
					icon={<DataIcon className="h-5 w-5"></DataIcon>}
					title={"Data"}
				>
					<div className="flex flex-col items-start justify-center gap-4 w-full">
						<div className="flex flex-col items-start justify-center gap-4 p-4 border border-dashed w-full"></div>
					</div>
				</BoxWithIcon>
			</div>
			<UploadButton
				buttonType="File"
				onUploadSuccess={handleFileChange}
				onClear={() => setFileSrc(undefined)}
			></UploadButton>
			<div className="flex flex-col items-start justify-center gap-4 w-full">
				<ActionPanel
					primaryButtonLabel={"Create"}
					primaryButtonEnabled={true}
					secondaryButtonLabel={"Cancel"}
					secondaryButtonEnabled={true}
					primaryButtonAction={function (): void {
						throw new Error("Function not implemented.");
					}}
					secondaryButtonAction={onClose}
				></ActionPanel>
			</div>
		</div>
	);
};

export default CreateCampaign;
